import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_FULL, LOGIN_BG, formatApiError } from "@/lib/api";
import { useBranding } from "@/hooks/useBranding";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const { branding, asset } = useBranding();
  const fullLogo = branding?.logo_full_url ? asset(branding.logo_full_url) : LOGO_FULL;
  const bizName = branding?.trading_name || "True Seal Caulking";
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/dashboard" : user.role === "contractor" ? "/onboarding" : "/my-jobs", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "admin" ? "/dashboard" : u.role === "contractor" ? "/onboarding" : "/my-jobs", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-navy relative px-4 py-10">
      <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${LOGIN_BG})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/70 via-navy/80 to-navy-dark/95" />

      <div className="relative z-10 w-full max-w-md animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <img src={fullLogo} alt={bizName} className="h-28 object-contain" />
          <p className="mt-3 text-slate-300 text-sm tracking-[0.25em] uppercase">Sealed Right. Built To Last.</p>
        </div>

        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 text-center mb-6">Sign in to your account</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Mail className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address" className="w-full pl-11 pr-4 py-3 rounded-lg bg-slate-100 border border-slate-200 focus:border-navy focus:bg-white outline-none transition-colors" />
            </div>
            <div className="relative">
              <Lock className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="login-password-input" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Password" className="w-full pl-11 pr-11 py-3 rounded-lg bg-slate-100 border border-slate-200 focus:border-navy focus:bg-white outline-none transition-colors" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
            <button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full bg-navy hover:bg-navy-light text-white rounded-lg py-3 font-semibold transition-colors disabled:opacity-60">
              {loading ? "Signing in…" : "SIGN IN"}
            </button>
          </form>
          <p className="text-center text-xs text-slate-400 mt-6">{bizName} · Field Operations Platform</p>
        </div>
      </div>
    </div>
  );
}
